import "./style.css";
import { createApp } from "vue";
import App from "./App.vue";
import { createWebHistory, createRouter, RouteRecordRaw } from "vue-router";
import DashboardLayout from "./layouts/dashboard.vue";
import { createPinia } from "pinia";
import { createI18n } from "vue-i18n";
import messages from "@intlify/unplugin-vue-i18n/messages";
import { isFeatureEnabled } from "./utils/feature-flags";

// Pages are lazily imported so each route ships as its own chunk.
const LoginPage = () => import("./pages/login.vue");
const OnboardingPage = () => import("./pages/onboarding.vue");
const ForgotPasswordPage = () => import("./pages/forgot-password.vue");
const ResetPasswordPage = () => import("./pages/reset-password.vue");
const BlockedPage = () => import("./pages/blocked.vue");
const PoliciesPage = () => import("./pages/dashboard/protection/policies.vue");
const PolicyEditorPage = () =>
  import("./pages/dashboard/protection/policy-editor.vue");
const Settings = () => import("./pages/dashboard/account/settings.vue");
const WaitPage = () => import("./pages/wait.vue");
const AssistantPage = () => import("./pages/dashboard/protection/assistant.vue");
const ErrorPage = () => import("./pages/error.vue");
const InstallPage = () => import("./pages/install.vue");
const OidcMicrosoftAdminConsentPage = () =>
  import("./pages/oidc-microsoft-admin-consent.vue");
const SecureBrowserPage = () => import("./pages/secure-browser.vue");
const SecureBrowser = () =>
  import("./pages/dashboard/protection/secure-browser.vue");
const CoursesPage = () => import("./pages/dashboard/e-learning/courses.vue");
const CourseDetailsPage = () =>
  import("./pages/dashboard/e-learning/courses/[courseId].vue");
const QuizDetailsPage = () =>
  import("./pages/dashboard/e-learning/courses/[courseId]/quiz/[quizId].vue");
const AttemptPage = () =>
  import(
    "./pages/dashboard/e-learning/courses/[courseId]/quiz/[quizId]/attempt/[attemptId].vue"
  );
const UsersPage = () => import("./pages/organization/users.vue");
const UserProfilePage = () => import("./pages/organization/user-profile.vue");
const UserRiskPage = () => import("./pages/organization/user-risk.vue");
const GroupsPage = () => import("./pages/organization/groups.vue");
const ConfigurationPage = () =>
  import("./pages/organization/configuration.vue");
const ManagedOrgsPage = () => import("./pages/organization/managed-orgs.vue");
const SubscriptionPage = () => import("./pages/organization/subscription.vue");
const DashboardPage = () => import("./pages/dashboard/dashboard.vue");
const ManagementPage = () =>
  import("./pages/dashboard/e-learning/management.vue");
const ManageCoursePage = () =>
  import("./pages/dashboard/e-learning/management/[courseId].vue");
const CampaignsPage = () => import("./pages/dashboard/phishing/campaigns.vue");
const CampaignDetailsPage = () =>
  import("./pages/dashboard/phishing/campaign-details.vue");
const CreateCampaignPage = () =>
  import("./pages/dashboard/phishing/create-campaign.vue");
const SuspendedPage = () => import("./pages/suspended.vue");
const OnboardingCardPage = () => import("./pages/onboarding-card.vue");
const TrialPage = () => import("./pages/trial.vue");
const TrialConfirmPage = () => import("./pages/trial-confirm.vue");
const TrialSetupPage = () => import("./pages/trial-setup.vue");
const RealTimeProtectionEventsPage = () =>
  import("./pages/dashboard/protection/real-time-protection-events.vue");
const SecurityPolicyViolationsEventsPage = () =>
  import("./pages/dashboard/protection/security-policy-violations-events.vue");

import MdiShieldOutline from "~icons/mdi/shield-outline";
import MdiRobotOutline from "~icons/mdi/robot-outline";
import MdiComputer from "~icons/mdi/computer";
import MdiBookEducationOutline from "~icons/mdi/book-education-outline";
import MdiCogOutline from "~icons/mdi/cog-outline";
import MdiAccountGroupOutline from "~icons/mdi/account-group-outline";
import MdiAccountOutline from "~icons/mdi/account-outline";
import MdiDomain from "~icons/mdi/domain";
import MdiCreditCardOutline from "~icons/mdi/credit-card-outline";
import MdiViewDashboardOutline from "~icons/mdi/view-dashboard-outline";
import MdiVideoOutline from "~icons/mdi/video-outline";
import MdiListStatus from "~icons/mdi/list-status";
import MdiFish from "~icons/mdi/fish";
import MdiMagicStaff from "~icons/mdi/magic-staff";
import MdiShieldAlertOutline from "~icons/mdi/shield-alert-outline";
import MdiAccountAlertOutline from "~icons/mdi/account-alert-outline";
import { Role } from "tf-api-client";
import { useAuthStore } from "./stores/auth.store";
import {
  AWARENESS_ROUTE,
  TRIAL_CONFIRM_ROUTE,
  TRIAL_ROUTE,
  TRIAL_SETUP_ROUTE,
} from "./config/constants";
import {
  registerSessionHandler,
  ONBOARDING_CARD_ROUTE,
  SUSPENDED_ROUTE,
} from "./services/session-handler";
import { useTheme } from "./composables/use-theme";
import initPostHog from "./utils/posthog";
import { captureHandoffCode } from "./services/landing-api";

// Helper function to filter routes based on feature flags
const filterEnabledRoutes = (routes: any[], basePath: string) => {
  return routes.filter((route) => {
    const fullPath = `${basePath}/${route.path}`.replace(/\/+/g, "/");
    return isFeatureEnabled(fullPath);
  });
};

const protectionRoutes = [
  {
    path: "real-time-protection-events",
    component: RealTimeProtectionEventsPage,
    meta: {
      icon: MdiListStatus,
      section: "monitoring",
      roles: [Role.OrgAdmin, Role.SuperAdmin],
    },
  },
  {
    path: "security-policy-violation-events",
    component: SecurityPolicyViolationsEventsPage,
    meta: {
      icon: MdiShieldAlertOutline,
      section: "monitoring",
      roles: [Role.OrgAdmin, Role.SuperAdmin],
    },
  },
  {
    path: "policies",
    component: PoliciesPage,
    meta: {
      icon: MdiShieldOutline,
      roles: [Role.OrgAdmin, Role.SuperAdmin],
    },
  },
  // Policy editor (no icon: not shown in the sidebar)
  {
    path: "policies/new",
    component: PolicyEditorPage,
    meta: { roles: [Role.OrgAdmin, Role.SuperAdmin] },
  },
  {
    path: "policies/:policyId",
    component: PolicyEditorPage,
    meta: { roles: [Role.OrgAdmin, Role.SuperAdmin] },
  },
  {
    path: "assistant",
    component: AssistantPage,
    meta: {
      icon: MdiRobotOutline,
    },
  },
  {
    path: "secure-browser",
    component: SecureBrowser,
    meta: {
      icon: MdiComputer,
    },
  },
];

const accountRoutes = [{ path: "settings", component: Settings }];

const elearningRoutes = [
  {
    path: "courses",
    component: CoursesPage,
    meta: {
      icon: MdiBookEducationOutline,
    },
  },
  { path: "courses/:courseId", component: CourseDetailsPage },
  { path: "courses/:courseId/quiz/:quizId", component: QuizDetailsPage },
  {
    path: "courses/:courseId/quiz/:quizId/attempt/:attemptId",
    component: AttemptPage,
  },
  {
    path: "management",
    component: ManagementPage,
    meta: {
      icon: MdiVideoOutline,
      roles: [Role.OrgAdmin, Role.SuperAdmin],
    },
  },
  {
    path: "management/:courseId",
    component: ManageCoursePage,
  },
];

const organizationRoutes = [
  {
    path: "users",
    component: UsersPage,
    meta: { icon: MdiAccountOutline, roles: [Role.OrgAdmin, Role.SuperAdmin] },
  },
  {
    path: "user-risk",
    component: UserRiskPage,
    meta: {
      icon: MdiAccountAlertOutline,
      section: "monitoring",
      roles: [Role.OrgAdmin, Role.SuperAdmin],
    },
  },
  {
    path: "groups",
    component: GroupsPage,
    meta: {
      icon: MdiAccountGroupOutline,
      roles: [Role.OrgAdmin, Role.SuperAdmin],
    },
  },
  {
    path: "managed-orgs",
    component: ManagedOrgsPage,
    meta: {
      icon: MdiDomain,
      roles: [Role.OrgAdmin, Role.SuperAdmin],
      requiresManagerOrg: true,
    },
  },
  {
    // Standard, Manager and Managed organizations (the page adapts to the
    // kind). Flag: VITE_FEATURE_ORGANIZATION_SUBSCRIPTION.
    path: "subscription",
    component: SubscriptionPage,
    meta: {
      icon: MdiCreditCardOutline,
      roles: [Role.OrgAdmin, Role.SuperAdmin],
    },
  },
  {
    path: "configuration",
    component: ConfigurationPage,
    meta: {
      icon: MdiCogOutline,
      roles: [Role.OrgAdmin, Role.SuperAdmin],
    },
  },
];

const organizationDetailRoutes = [
  {
    path: "/organization/users/:userId",
    component: UserProfilePage,
    meta: { roles: [Role.OrgAdmin, Role.SuperAdmin] },
  },
  // Old address of the user profile, kept so existing links still work.
  {
    path: "/organization/users/:userId/risk-score",
    redirect: (to: { params: Record<string, unknown> }) =>
      `/organization/users/${to.params.userId}`,
  },
];

const phishingRoutes = [
  {
    path: "campaigns",
    component: CampaignsPage,
    meta: { icon: MdiFish, roles: [Role.OrgAdmin, Role.SuperAdmin] },
  },
  {
    path: "campaign-details",
    component: CampaignDetailsPage,
    meta: {
      roles: [Role.OrgAdmin, Role.SuperAdmin],
    },
  },
  {
    path: "new-campaign",
    component: CreateCampaignPage,
    meta: {
      icon: MdiMagicStaff,
      roles: [Role.OrgAdmin, Role.SuperAdmin],
    },
  },
];

// Filter routes based on feature flags
const filteredProtectionRoutes = filterEnabledRoutes(
  protectionRoutes,
  "/protection",
);
const filteredElearningRoutes = filterEnabledRoutes(
  elearningRoutes,
  "/e-learning",
);
const filteredPhishingRoutes = filterEnabledRoutes(phishingRoutes, "/phishing");
const filteredAccountRoutes = filterEnabledRoutes(accountRoutes, "/account");
const filteredOrganizationRoutes = filterEnabledRoutes(
  organizationRoutes,
  "/organization",
);

// Check if dashboard is enabled
const isDashboardEnabled = isFeatureEnabled("/dashboard");

// Collect all available top-level routes to determine default redirect
const availableRoutes = [
  isDashboardEnabled ? "/dashboard" : null,
  ...filteredProtectionRoutes.map((route) => `/protection/${route.path}`),
  //...filteredDefencesRoutes.map((route) => `/defences/${route.path}`),
  ...filteredElearningRoutes.map((route) => `/e-learning/${route.path}`),
  ...filteredAccountRoutes.map((route) => `/account/${route.path}`),
  ...filteredOrganizationRoutes.map((route) => `/organization/${route.path}`),
  ...filteredPhishingRoutes.map((route) => `/phishing/${route.path}`),
].filter(Boolean) as string[];

// Default to first available route or error page if none available
const defaultRedirect =
  availableRoutes.length > 0 ? availableRoutes[0] : "/error";

export const routes: RouteRecordRaw[] = [
  {
    path: "/",
    component: DashboardLayout,
    redirect: defaultRedirect,
    children: [
      ...(isDashboardEnabled
        ? [
            {
              path: "/dashboard",
              component: DashboardPage,
              meta: {
                icon: MdiViewDashboardOutline,
                roles: [Role.OrgAdmin, Role.SuperAdmin],
              },
            },
          ]
        : []),
      ...filteredProtectionRoutes.map((route) => ({
        ...route,
        path: `/protection/${route.path}`,
      })),
      ...filteredElearningRoutes.map((route) => ({
        ...route,
        path: `/e-learning/${route.path}`,
      })),
      ...filteredPhishingRoutes.map((route) => ({
        ...route,
        path: `/phishing/${route.path}`,
      })),
      ...filteredAccountRoutes.map((route) => ({
        ...route,
        path: `/account/${route.path}`,
      })),
      ...filteredOrganizationRoutes.map((route) => ({
        ...route,
        path: `/organization/${route.path}`,
      })),
      ...organizationDetailRoutes,
    ],
  },
  {
    path: "/login",
    component: LoginPage,
    meta: { skipAuthentication: true, autoRedirect: true },
  },
  {
    path: "/onboarding",
    component: OnboardingPage,
    meta: { skipAuthentication: true, autoRedirect: true },
  },
  // Self-service trial requests. Only where the backend has them enabled
  // (`VITE_FEATURE_TRIAL`); the pages also handle `trial_requests_disabled`.
  ...(isFeatureEnabled(TRIAL_ROUTE)
    ? [
        {
          path: TRIAL_ROUTE,
          component: TrialPage,
          meta: { skipAuthentication: true, autoRedirect: true },
        },
        // No auto-redirect: a signed-in user opening the confirmation link
        // still confirms the request.
        {
          path: TRIAL_CONFIRM_ROUTE,
          component: TrialConfirmPage,
          meta: { skipAuthentication: true },
        },
        {
          path: TRIAL_SETUP_ROUTE,
          component: TrialSetupPage,
          meta: { skipAuthentication: true, autoRedirect: true },
        },
      ]
    : []),
  {
    path: "/forgot-password",
    component: ForgotPasswordPage,
    meta: { skipAuthentication: true, autoRedirect: true },
  },
  {
    path: "/reset-password",
    component: ResetPasswordPage,
    meta: { skipAuthentication: true, autoRedirect: true },
  },
  {
    path: "/blocked",
    component: BlockedPage,
    meta: { skipAuthentication: true },
  },
  // Landing of the simulation emails, for anyone who followed their link,
  // logged in or not (the page uses a landing session, not the user's token).
  {
    path: AWARENESS_ROUTE,
    component: BlockedPage,
    meta: { skipAuthentication: true, awareness: true },
  },
  { path: "/secure-browser", component: SecureBrowserPage },
  { path: "/wait", component: WaitPage, meta: { skipAuthentication: true } },
  { path: "/error", component: ErrorPage, meta: { skipAuthentication: true } },
  // Full-screen page for admins of a suspended organization (restricted
  // token). Deliberately outside DashboardLayout and never behind a feature
  // flag: it must be reachable in every environment.
  { path: SUSPENDED_ROUTE, component: SuspendedPage },
  // Card step of a self-service trial (onboarding token only). Outside
  // DashboardLayout for the same reason: nothing else may fetch meanwhile.
  { path: ONBOARDING_CARD_ROUTE, component: OnboardingCardPage },
  { path: "/install", component: InstallPage },
  {
    path: "/oidc/microsoft/admin-consent",
    component: OidcMicrosoftAdminConsentPage,
    meta: { skipAuthentication: true },
  },
];

routes.push({ path: "/:pathMatch(.*)*", redirect: "/" });

const router = createRouter({
  history: createWebHistory(),
  routes: routes,
});

router.beforeEach((to) => {
  // The awareness page shows whatever the state of a session on this browser:
  // an admin in onboarding or suspended who clicked a simulation link too.
  if (to.meta.awareness) {
    return true;
  }

  const authStore = useAuthStore();
  const isLoggedIn = authStore.isLoggedIn();

  if (!isLoggedIn && !to.meta.skipAuthentication) {
    return "/login";
  }
  if (isLoggedIn && to.meta.skipAuthentication && to.meta.autoRedirect) {
    return "/";
  }

  // Self-service onboarding (card not confirmed yet): the token only grants
  // the card step, so every route leads there. Also covers the landing after
  // login and trial set-up. Runs before the frozen branch.
  if (isLoggedIn && authStore.onboarding) {
    return to.path === ONBOARDING_CARD_ROUTE ? true : ONBOARDING_CARD_ROUTE;
  }
  if (isLoggedIn && to.path === ONBOARDING_CARD_ROUTE) {
    return "/";
  }

  // Session-suspended (restricted token): everything, including deep links
  // and reloads, leads to the suspended page. Runs before the role check.
  if (isLoggedIn && authStore.suspended && to.path !== SUSPENDED_ROUTE) {
    return SUSPENDED_ROUTE;
  }
  if (isLoggedIn && !authStore.suspended && to.path === SUSPENDED_ROUTE) {
    return "/";
  }

  const userRoles = authStore.effectiveRole || [];
  if (
    isLoggedIn &&
    to.meta.roles &&
    !(to.meta.roles as Role[]).some((role: Role) => userRoles.includes(role))
  ) {
    return "/protection/assistant";
  }

  return true;
});

const i18n = createI18n({
  locale: localStorage.getItem("locale") || "en",
  fallbackLocale: "en",
  messages,
});

// Per-route document titles. Sidebar label keys already exist for every
// navigable section, so they double as page titles; dynamic param segments
// (ids) are skipped so "/e-learning/courses/<uuid>" titles as "Courses".
const APP_TITLE = "TeamFence";
router.afterEach((to) => {
  const paramValues = Object.values(to.params).flat() as string[];
  const segments = to.path.split("/").filter(Boolean);
  while (
    segments.length > 0 &&
    paramValues.includes(segments[segments.length - 1])
  ) {
    segments.pop();
  }
  const segment = segments.pop();
  if (!segment) {
    document.title = `${APP_TITLE} | Dashboard`;
    return;
  }
  const key = `partials.sidebar.${segment}`;
  const label = i18n.global.te(key)
    ? i18n.global.t(key)
    : segment
        .replace(/[-_]+/g, " ")
        .replace(/\b\p{L}/gu, (char) => char.toUpperCase());
  document.title = `${label} | ${APP_TITLE}`;
});

async function bootstrap() {
  // First: the handoff code of the awareness page leaves the URL before any
  // script can record it. PostHog doesn't run on that page.
  captureHandoffCode();
  if (window.location.pathname !== AWARENESS_ROUTE) {
    initPostHog();
  }

  // Initial <html lang>; the dashboard layout keeps it in sync afterwards.
  document.documentElement.lang = localStorage.getItem("locale") || "en";

  const app = createApp(App);

  const pinia = createPinia();
  app.use(pinia);

  const authStore = useAuthStore();
  // before init(): a refresh failing during init must still be handled
  registerSessionHandler(router);
  await authStore.init();
  useTheme();

  app.use(i18n);
  app.use(router);
  app.mount("#app");
}

bootstrap();
